/**
 * The shape of the documentation tree, and the files a person hands it.
 *
 * Kept apart from the page so the rules — what a name may be, which files an upload takes, how a
 * dropped folder becomes paths — can be tested without a browser.
 */

import type { DocFolder, DocSummary, DocTree } from './types'

/** The server's own limits, repeated so the page can say so before sending anything. */
export const DOC_MAX_BYTES = 512 * 1024
export const DOC_NAME_MAX = 120
/** Files per upload request; the server takes up to 200, smaller batches keep progress visible. */
export const UPLOAD_BATCH = 50
/** Bytes per upload request: the edge refuses bodies over 4 MB, and a batch must never be one. */
export const UPLOAD_BATCH_BYTES = 3 * 1024 * 1024

/** Uploads in requests the edge will take: at most so many files, and so many bytes, each. */
export function uploadBatches(files: PickedFile[], maxFiles = UPLOAD_BATCH, maxBytes = UPLOAD_BATCH_BYTES): PickedFile[][] {
  const batches: PickedFile[][] = []
  let current: PickedFile[] = []
  let bytes = 0
  for (const picked of files) {
    if (current.length > 0 && (current.length >= maxFiles || bytes + picked.file.size > maxBytes)) {
      batches.push(current)
      current = []
      bytes = 0
    }
    current.push(picked)
    bytes += picked.file.size
  }
  if (current.length > 0) batches.push(current)
  return batches
}

export interface TreeNode {
  folder: DocFolder | null
  folders: TreeNode[]
  docs: DocSummary[]
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/** The flat lists as a tree, folders first and everything in natural order ("2" before "10"). */
export function buildTree(tree: DocTree): TreeNode {
  const nodes = new Map<string, TreeNode>()
  for (const folder of tree.folders) nodes.set(folder.id, { folder, folders: [], docs: [] })

  const root: TreeNode = { folder: null, folders: [], docs: [] }
  for (const node of nodes.values()) {
    const parent = node.folder!.parentId ? nodes.get(node.folder!.parentId) : undefined
    ;(parent ?? root).folders.push(node)
  }
  for (const doc of tree.docs) (doc.folderId ? nodes.get(doc.folderId) ?? root : root).docs.push(doc)

  const sort = (node: TreeNode): void => {
    node.folders.sort((a, b) => collator.compare(a.folder!.name, b.folder!.name))
    node.docs.sort((a, b) => collator.compare(a.name, b.name))
    node.folders.forEach(sort)
  }
  sort(root)
  return root
}

/** A folder and the folders above it, root first — for a breadcrumb and for opening the tree to it. */
export function ancestorsOf(folders: DocFolder[], folderId: string | null): DocFolder[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]))
  const chain: DocFolder[] = []
  for (let cursor = folderId ? byId.get(folderId) : undefined; cursor && chain.length < 64; cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined) {
    chain.unshift(cursor)
  }
  return chain
}

/** A folder and everything under it: where it cannot be moved to. */
export function descendantsOf(folders: DocFolder[], folderId: string): Set<string> {
  const found = new Set([folderId])
  let grew = true
  while (grew) {
    grew = false
    for (const folder of folders) {
      if (folder.parentId && found.has(folder.parentId) && !found.has(folder.id)) {
        found.add(folder.id)
        grew = true
      }
    }
  }
  return found
}

/** The same rule the server applies to a folder or file name. */
export function isValidSegment(name: string): boolean {
  return (
    name.trim() === name &&
    name.length > 0 &&
    name.length <= DOC_NAME_MAX &&
    name !== '.' &&
    name !== '..' &&
    !/[/\\:*?"<>|]/.test(name) &&
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u001f\u007f-\u009f]/.test(name)
  )
}

/** What a person typed as a document name, as one: trimmed, ".md" added when missing. */
export function asDocName(input: string): string {
  const name = input.trim()
  return /\.md$/i.test(name) ? name : `${name}.md`
}

export function isValidDocName(name: string): boolean {
  return isValidSegment(name) && /\.md$/i.test(name) && name.length > 3
}

export interface PickedFile {
  file: File
  /** Relative to where it is being uploaded: "guides/setup.md" for a file in a dropped folder. */
  path: string
}

/** Markdown files within the size limit go up; the rest are named back to the person, not sent. */
export function partitionUploads(files: PickedFile[]): { markdown: PickedFile[]; skipped: Array<{ path: string; reason: 'not_markdown' | 'too_large' }> } {
  const markdown: PickedFile[] = []
  const skipped: Array<{ path: string; reason: 'not_markdown' | 'too_large' }> = []
  for (const picked of files) {
    if (!/\.md$/i.test(picked.path)) skipped.push({ path: picked.path, reason: 'not_markdown' })
    else if (picked.file.size > DOC_MAX_BYTES) skipped.push({ path: picked.path, reason: 'too_large' })
    else markdown.push(picked)
  }
  return { markdown, skipped }
}

/** Files chosen in a file input — with their folder path when a whole folder was chosen. */
export function filesFromInput(list: FileList | File[]): PickedFile[] {
  return Array.from(list).map((file) => ({ file, path: cleanPath((file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name) }))
}

/**
 * Files dropped on the page, walking into dropped folders so their structure comes along.
 * Browsers without folder entries get the files alone.
 */
export async function filesFromDrop(transfer: DataTransfer): Promise<PickedFile[]> {
  const entries = Array.from(transfer.items ?? [])
    .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
    .filter((entry): entry is FileSystemEntry => entry !== null)

  if (entries.length === 0) return filesFromInput(transfer.files)

  const picked: PickedFile[] = []
  const walk = async (entry: FileSystemEntry, prefix: string, depth: number): Promise<void> => {
    if (depth > 12 || picked.length >= 2000) return
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject))
      picked.push({ file, path: cleanPath(`${prefix}${entry.name}`) })
      return
    }
    if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      // readEntries answers in pages; an empty page means the end.
      for (;;) {
        const page = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject))
        if (page.length === 0) break
        for (const child of page) await walk(child, `${prefix}${entry.name}/`, depth + 1)
      }
    }
  }
  for (const entry of entries) await walk(entry, '', 0)
  return picked
}

function cleanPath(path: string): string {
  return path.replace(/\\/g, '/').split('/').filter(Boolean).join('/')
}

export function formatBytes(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** UTF-8 size of the text, which is what the server counts. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length
}
